/**
 * List of all global  actions.
 */
export enum ActionState {
  GET_APP_CONFIG = "[core app] Get App Config",
  AUTO_LOGIN = "[core app] Auto Login",
  APP_LOGOUT = "[core app] App Logout",
  SET_APPLICATIONS = "[core app] Set Applications",
  SET_ACTIVE_APP = "[core app] Set Active App",
  SET_USER_TOKEN = "[core app] set user token",
  GET_COMPANY_ID = "[core app] get Company Id",
  FETCH_USER_INFO = "[core app] fetch user info",
  SET_USER_INFO = "[core app] set user info",
  SET_INTER_APP_DATA = "[core app] set inter app data",
  SET_LOADER_STATUS = "[core app] set loader status",
  SET_APP_TITLE = "[core app] set app title",
  SET_PAGE_TITLE = "[core app] set page title",
  SET_ACTIVE_MENU = "[core app] set active menu",
  SET_SETTINGS_MENU = "[core app] set settings menu",
  SET_SETTINGS_TYPE = "[core app] set settings type",
  SET_APPLICATION_MESSAGES = "[core app] set application messages",
  SET_APPLICATION_MESSAGES_SUCCESS = "[core app] set application messages success",
  SET_APP_LOGOUT_SUCCESS = "[core app] App Logout Success",
  GET_PENDING_EULAS = "[core app] get pending eulas",
  SET_PENDING_EULAS = "[core app] set pending eulas",
  SET_ID_TOKEN = "[core app] set id token",
  SET_FONE_ALERT_CONFIG = "[core app] set fone alert config",
  FETCH_PROVISIONING_MESSAGES = "[core app] fetch provisioning messages",
  SET_PROVISIONING_MESSAGES = "[core app] set provisioning messages",
  SET_SHOW_HOMEPAGE = "[core app] set show homepage",
  SET_USE_NEW_IFRAME = "[core app] set use new iframe",
  SET_USE_SSE_EU_URL = "[core app] set sse eu url",
  TOGGLE_AI_PANEL = "[core app] toggle ai panel",
  SET_AI_PANEL_ALERTS = "[core app] set ai panel alerts",
  REMOVE_AI_PANEL_ALERT = "[core app] remove ai panel alert",
  SET_IS_AI_ENABLED = "[core app] set is ai enabled",
  CLEAR_AI_PANEL_ALERTS = "[core app] clear ai panel alerts",
  LOCAL_AUTH_REQUIRED = "[core app] local auth required"
}

export interface AIPanelState {
  isOpen: boolean;
  isEnabled: boolean;
  alerts: Record<string, any>[];
  /**
   * Context an application page publishes to drive a custom ARIA surface
   * (app-driven guidance UI). The page sets this via
   * coreActions.setAISurfaceContext; ARIA reads it and renders the matching
   * surface. Shape is intentionally generic (kept opaque to coreui) — the
   * ARIA/consuming side owns the concrete contract. Null ⇒ no surface.
   */
  surfaceContext: Record<string, any> | null;
}

/**
 * Core application state interface
 */
export interface CoreState {
  /** List of available applications */
  applications: any[];
  /** Currently active application */
  activeApp: any | null;
  /** User authentication token */
  userToken: string | null;
  /** Company identifier */
  companyId: string | number | null;
  /** User information object - complex user data */
  userInfo: Record<string, any> | null;
  /** Inter-application communication data - shared data between microfrontends */
  interAppData: Record<string, any> | null;
  /** Global loader visibility flag */
  showGlobalLoader: boolean;
  /** Homepage visibility flag */
  showHomepage: boolean;
  /** Application title */
  appTitle: string | null;
  /** Page title */
  pageTitle: string | null;
  /** List of mounted applications */
  mountedApps: any[];
  /** Currently active menu item */
  activeMenu: any | null;
  /** Settings menu configuration */
  settingsMenu: any[];
  /** Settings page flag */
  isSettingsPage: boolean;
  /** Application messages collection - message data by key */
  applicationMessages: Record<string, any>;
  /** EULA configuration object - end user license agreement config */
  eulaConfig: Record<string, any> | null;
  /** ID token for authentication */
  idToken: string | null;
  /** Encoded ID token string */
  idTokenEncoded: string | null;
  /** Initial-load flag — set during logout reset */
  initialLoad: boolean;
  /** White loader visibility flag */
  showWhiteLoader: boolean;
  /** Environment identifier */
  environment: string | null;
  /** FONE alert configuration */
  foneAlertConfig: {
    status: boolean;
    alertProps: Record<string, any> | null;
  };
  /** iframe URL */
  iframeURL: string | null;
  /** Provisioning messages collection */
  provisioningMessages: any[];
  /** New iframe usage flag */
  useNewIframe: boolean;
  /** SSE EU URL usage flag */
  useSSEEUURL: boolean;
  /** AI Panel state */
  aiPanel: AIPanelState | null;
  /** Local auth required flag — set when running on localhost without valid token */
  localAuthRequired: boolean;
  /** Active app's module URL prefix (routing prefix) */
  routePrefix: string | null;
  /** Active app's module-URL prefix (API/app prefix) */
  appPrefix: string | null;
}

/**
 * Initial state
 */
export const initialState: CoreState = {
  applications: [],
  activeApp: null,
  userToken: null,
  companyId: null,
  userInfo: null,
  interAppData: null,
  showGlobalLoader: true,
  showHomepage: false,
  appTitle: null,
  pageTitle: null,
  mountedApps: [],
  activeMenu: null,
  settingsMenu: [],
  isSettingsPage: false,
  applicationMessages: {},
  eulaConfig: null,
  idToken: null,
  idTokenEncoded: null,
  initialLoad: false,
  showWhiteLoader: false,
  environment: null,
  foneAlertConfig: {
    status: false,
    alertProps: null
  },
  iframeURL: null,
  provisioningMessages: [],
  useNewIframe: false,
  useSSEEUURL: false,
  aiPanel: {
    isOpen:false,
    isEnabled:false,
    alerts: [],
    surfaceContext: null
  },
  localAuthRequired: false,
  routePrefix: null,
  appPrefix: null
}
